import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Quote } from "lucide-react";
import { api } from "@/lib/api";

const Initials = ({ name }) => (
  <span className="w-14 h-14 shrink-0 rounded-xl bg-[#171715] text-[#D6AD62] flex items-center justify-center font-display font-semibold text-sm">
    {name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
  </span>
);

const ReviewCard = ({ r }) => (
  <figure className="relative flex flex-col rounded-2xl bg-white/70 border border-[#171715]/5 p-7 lg:p-8 transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_24px_50px_-30px_rgba(23,23,21,.35)]" data-testid={`review-${r.id}`}>
    <Quote size={28} className="absolute top-7 right-7 text-[#D6AD62]/40" />
    <figcaption className="flex items-center gap-4 pr-10" data-testid={`review-header-${r.id}`}>
      {r.avatar
        ? <img src={r.avatar} alt={r.company || r.name} className="w-14 h-14 shrink-0 rounded-xl object-contain bg-white border border-[#171715]/[0.07] p-1" data-testid={`review-avatar-${r.id}`} />
        : <Initials name={r.company || r.name} />}
      <div className="min-w-0">
        <p className="font-display font-semibold text-base leading-tight">{r.company || r.name}</p>
        {r.company && <p className="text-xs text-[#77736B] mt-0.5">{r.name}</p>}
      </div>
    </figcaption>
    <div className="flex text-[#b58a3f] gap-0.5 mt-5">{Array.from({ length: r.rating }, (_, i) => <Star key={i} size={15} fill="currentColor" />)}</div>
    <blockquote className="text-[15px] leading-relaxed mt-3 flex-1 whitespace-pre-line">«{r.text}»</blockquote>
    {r.project_slug && <Link to={`/layihe/${r.project_slug}`} className="mt-6 self-start text-xs underline underline-offset-4 text-[#77736B] hover:text-[#171715]" data-testid={`review-project-${r.id}`}>Layihəyə bax</Link>}
  </figure>
);

export const GoogleG = ({ className = "w-7 h-7 shrink-0" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const GoogleBadge = ({ g }) => {
  const Tag = g.url ? "a" : "div";
  return (
    <Tag {...(g.url ? { href: g.url, target: "_blank", rel: "noopener noreferrer" } : {})} className="inline-flex items-center gap-4 rounded-2xl bg-white border border-[#171715]/[0.07] px-5 py-3.5 shadow-[0_18px_40px_-30px_rgba(23,23,21,.4)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5" data-testid="google-rating-badge">
      <GoogleG />
      <div>
        <div className="flex items-center gap-2">
          <span className="font-display text-2xl font-bold leading-none" data-testid="google-rating-value">{g.rating.toFixed(1)}</span>
          <span className="relative inline-flex text-[#E3DCCF]">
            {Array.from({ length: 5 }, (_, i) => <Star key={i} size={17} fill="currentColor" />)}
            <span className="absolute inset-0 flex overflow-hidden text-[#F4B400]" style={{ width: `${(g.rating / 5) * 100}%` }}>
              {Array.from({ length: 5 }, (_, i) => <Star key={i} size={17} fill="currentColor" className="shrink-0" />)}
            </span>
          </span>
        </div>
        <p className="text-xs text-[#77736B] mt-1" data-testid="google-rating-count">Google-da {g.count} rəy əsasında{g.url ? " · baxın →" : ""}</p>
      </div>
    </Tag>
  );
};

export default function Reviews() {
  const [list, setList] = useState([]);
  const [google, setGoogle] = useState(null);
  useEffect(() => {
    api.get("/reviews").then((r) => setList(r.data)).catch(() => {});
    api.get("/reviews/google").then((r) => setGoogle(r.data)).catch(() => {});
  }, []);
  if (!list.length && !google) return null;
  return (
    <section id="reviews" data-testid="reviews-section" className="py-20 md:py-24 scroll-mt-16">
      <div className="container-lm">
        <p className="eyebrow">Rəylər</p>
        <div className="flex flex-wrap items-end justify-between gap-6 mt-4">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">Müştərilərimiz nə deyir</h2>
          {google && <GoogleBadge g={google} />}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-12">
          {list.map((r) => <ReviewCard key={r.id} r={r} />)}
        </div>
      </div>
    </section>
  );
}
